/**
 * Kategorisida – listar alla regelsidor inom en kategori (t.ex. /pbl).
 */
import React from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowRight, Sparkles, ChevronRight, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { CATEGORIES, CATEGORY_ORDER } from "@/content/rulebank";
import { combineJsonLd, organizationJsonLd } from "@/lib/structuredData";

const SITE = "https://bygglovkollen.se";

export default function RuleCategory() {
  const location = useLocation();
  const category = location.pathname.split("/").filter(Boolean)[0];
  const cat = CATEGORIES[category];
  if (!cat) return <Navigate to="/" replace />;

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Start", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: cat.label, item: `${SITE}/${cat.slug}` },
    ],
  };

  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: cat.h1,
    description: cat.metaDescription,
    inLanguage: "sv-SE",
    url: `${SITE}/${cat.slug}`,
    hasPart: cat.rules.map((r) => ({
      "@type": "Article",
      name: r.h1 || r.title,
      url: `${SITE}/${cat.slug}/${r.slug}`,
      description: r.metaDescription,
    })),
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <Seo
        title={cat.label}
        description={cat.metaDescription}
        path={`/${cat.slug}`}
        jsonLd={combineJsonLd(breadcrumbLd, organizationJsonLd, collectionLd)}
      />

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6" aria-label="Brödsmulor">
        <Link to="/" className="hover:text-blue-700">Start</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-zinc-900 font-medium">{cat.label}</span>
      </nav>

      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-700 mb-3">
        <Sparkles className="w-3.5 h-3.5" /> Regelbanken
      </div>
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
        {cat.h1}
      </h1>
      <p className="mt-4 text-base sm:text-lg text-zinc-600 max-w-3xl leading-relaxed">{cat.intro}</p>

      {/* CTA: Öppna fulltext för PBL/Miljöbalken */}
      {(cat.slug === "pbl") && (
        <Link
          to="/lag/pbl"
          data-testid="cta-fulltext-pbl"
          className="mt-6 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-sm text-sm font-medium transition"
        >
          <ShieldCheck className="w-4 h-4" />
          Läs hela PBL (live från Riksdagen)
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}
      {(cat.slug === "bbr") && (
        <Link
          to="/bbr-krav"
          data-testid="cta-bbr-tables"
          className="mt-6 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-sm text-sm font-medium transition"
        >
          <ShieldCheck className="w-4 h-4" />
          Öppna BBR-Tabellbanken
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}

      {/* Rules list */}
      <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid={`rules-${cat.slug}`}>
        {cat.rules.map((r) => (
          <Link
            key={r.slug}
            to={`/${cat.slug}/${r.slug}`}
            data-testid={`rule-link-${r.slug}`}
            className="group bg-white border border-zinc-200 rounded-sm p-5 hover:border-blue-400 hover:shadow-sm transition"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{cat.short}</div>
              <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-blue-700 group-hover:translate-x-0.5 transition" />
            </div>
            <h2 className="text-base font-semibold text-zinc-900 group-hover:text-blue-700 transition leading-tight">
              {r.h1 || r.title}
            </h2>
            <p className="mt-2 text-sm text-zinc-600 line-clamp-2">{r.metaDescription}</p>
          </Link>
        ))}
      </div>

      {/* Other categories */}
      <section className="mt-16 pt-8 border-t border-zinc-200">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Utforska mer</div>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_ORDER.filter((c) => c !== category).map((c) => (
            <Link
              key={c}
              to={`/${c}`}
              className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-blue-700 hover:text-blue-700 rounded-sm px-3 py-1.5 text-sm text-zinc-700 transition"
            >
              {CATEGORIES[c].label}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
